import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useId, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { type FunnelStatus } from 'src/api/generated';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import {
  PiCheck,
  PiTrash,
  PiX,
  PiCopyLight,
  PiCheckBold,
  PiDotsThreeVertical,
  PiFileTextLight,
  PiListBullets,
  PiLock,
} from 'react-icons/pi';
import { FunnelStatusDescriptionBlocks } from './status-description-blocks';
import { FunnelStatusDescriptionEditor } from './editor/status-description-editor';
import { AiBadge } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import { useCopyToClipboard } from 'src/modules/shared/hooks/copy-to-clipboard';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import type { DropdownItem } from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import { type StatusReference } from '../../../../types/funnels/statuses/status-description.types';
import {
  composeDescription,
  parseDescription,
} from '../../../../utils/funnels/statuses/status-description.utils';
import { applyEditorTextFeatures } from 'src/modules/shared/utils/editor/editor-text.util';
import type {
  EditorKey,
  EditorKeyEventFlags,
  EditorTextFeaturesConfig,
  EditorTextState,
} from 'src/modules/shared/types/editor/editor.type';

const plainDescriptionFeatures: EditorTextFeaturesConfig = {
  bulletLists: true,
  numberedLists: true,
  nestedNumberedLists: true,
  indentWithTab: true,
  outdentWithShiftTab: true,
  continueListOnEnter: true,
  exitListOnEmptyEnter: true,
  convertNumberedToNestedBulletOnTab: false,
  increaseBulletNestingOnTab: true,
  decreaseIndentOnShiftTab: true,
  allowPlainNewline: true,
  allowPlainTab: false,
};

const funnelStatusFormSchema = z.object({
  // TODO: check when implementing i18n
  name: z.string().min(1, 'El nombre es requerido'),
  description: z.string().optional(),
  terminalOutcome: z.enum(['', 'positive', 'negative']),
});

export type FunnelStatusFormValues = z.infer<typeof funnelStatusFormSchema>;

interface FunnelStatusFormProps {
  status?: FunnelStatus;
  onSubmit: (data: FunnelStatusFormValues) => Promise<void> | void;
  id?: string;
  editMode?: boolean;
  isLoading?: boolean;
  onDelete?: () => void;
  showDelete?: boolean;
  onCancel?: () => void;
  referenceStatuses?: StatusReference[];
  onDirtyChange?: (dirty: boolean) => void;
  hideActions?: boolean;
  isAiLinked?: boolean;
}

const updateDescriptionFieldOptions = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
} as const;

interface StatusFormActionOptions {
  editMode: boolean;
  hideActions: boolean;
  isPlainMode: boolean;
  isCopied: boolean;
  hasDescriptionToCopy: boolean;
  onTogglePlainMode: () => void;
  onCopyDescription: () => void;
}

function createStatusFormActionItems({
  editMode,
  hideActions,
  isPlainMode,
  isCopied,
  hasDescriptionToCopy,
  onTogglePlainMode,
  onCopyDescription,
}: StatusFormActionOptions): DropdownItem[] {
  if (hideActions) return [];

  if (editMode) {
    return [
      {
        content: isPlainMode ? 'Secciones' : 'Texto plano',
        icon: isPlainMode ? (
          <PiListBullets className="h-4 w-4 text-gray-600" />
        ) : (
          <PiFileTextLight className="h-4 w-4 text-gray-600" />
        ),
        onClick: onTogglePlainMode,
      },
    ];
  }

  if (!hasDescriptionToCopy) return [];

  return [
    {
      content: isCopied ? '¡Copiado!' : 'Copiar',
      icon: isCopied ? (
        <PiCheckBold className="h-4 w-4 text-success-500" />
      ) : (
        <PiCopyLight className="h-4 w-4 text-gray-600" />
      ),
      onClick: onCopyDescription,
    },
  ];
}

function applyPlainDescriptionShortcut(
  event: React.KeyboardEvent<HTMLTextAreaElement>,
  disabled: boolean,
  onChange: (value: string) => void
) {
  if (disabled) return;
  if (event.key !== 'Enter' && event.key !== 'Tab') return;

  const target = event.currentTarget;
  const state: EditorTextState = {
    text: target.value,
    selection: {
      start: target.selectionStart,
      end: target.selectionEnd,
    },
  };
  const flags: EditorKeyEventFlags = {
    shiftKey: event.shiftKey,
    altKey: event.altKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
  };
  const result = applyEditorTextFeatures(
    state,
    event.key as EditorKey,
    flags,
    plainDescriptionFeatures
  );

  if (!result) return;

  event.preventDefault();
  target.value = result.text;
  target.setSelectionRange(result.selection.start, result.selection.end);
  onChange(result.text);
}

export function FunnelStatusForm({
  status,
  onSubmit,
  id,
  editMode = true,
  isLoading = false,
  onDelete,
  showDelete = false,
  onCancel,
  referenceStatuses,
  onDirtyChange,
  hideActions = false,
  isAiLinked = false,
}: FunnelStatusFormProps) {
  const { t } = useI18n();
  const terminalOutcomeInputId = useId();
  const initialRawDescription = status?.description ?? '';
  const normalizedInitialDescription = useMemo(
    () => composeDescription(parseDescription(initialRawDescription)),
    [initialRawDescription]
  );

  const [isPlainMode, setIsPlainMode] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting, isDirty },
    reset,
  } = useForm<FunnelStatusFormValues>({
    resolver: zodResolver(funnelStatusFormSchema),
    defaultValues: {
      name: status?.name ?? '',
      description: normalizedInitialDescription,
      terminalOutcome: status?.terminalOutcome ?? '',
    },
  });

  const descriptionValue = watch('description') ?? '';
  const descriptionSource = editMode
    ? descriptionValue
    : status?.description ?? '';
  const plainDescriptionForCopy = descriptionSource.trim();
  const { copyToClipboard, isCopied } = useCopyToClipboard();
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const submitHandler = async (data: FunnelStatusFormValues) => {
    await onSubmit(data);
    // After successful submit, set current values as new baseline to clear dirty state
    reset({
      name: data.name,
      description: data.description ?? '',
      terminalOutcome: data.terminalOutcome,
    });
  };

  const handleCopyDescription = () => {
    if (!plainDescriptionForCopy) return;
    void copyToClipboard(plainDescriptionForCopy);
  };

  const handleDescriptionChange = (value: string) =>
    setValue('description', value, updateDescriptionFieldOptions);

  const actionItems = createStatusFormActionItems({
    editMode,
    hideActions,
    isPlainMode,
    isCopied,
    hasDescriptionToCopy: Boolean(plainDescriptionForCopy),
    onTogglePlainMode: () => setIsPlainMode((prev) => !prev),
    onCopyDescription: handleCopyDescription,
  });

  const actionsMenu =
    actionItems.length === 0 ? null : (
      <MenuDropdown
        size="sm"
        placement="bottom-end"
        items={actionItems}
        handlerComponent={
          <Button
            type="button"
            variant="neutral"
            appearance="ghost"
            size="xs"
            isIconOnly
            className="text-gray-500 hover:text-gray-700"
          >
            {isCopied ? (
              <PiCheckBold className="h-4 w-4 text-success-500" />
            ) : (
              <PiDotsThreeVertical className="h-4 w-4" />
            )}
          </Button>
        }
      />
    );

  const handleCancel = () => {
    // Reset to initial values to clear dirty state before closing
    reset({
      name: status?.name ?? '',
      description: normalizedInitialDescription,
      terminalOutcome: status?.terminalOutcome ?? '',
    });
    onCancel?.();
  };

  if (!editMode) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isAiLinked && <AiBadge size="sm" appearance="outline" />}

            <FunnelStatusName
              name={status?.name ?? ''}
              outcome={status?.terminalOutcome}
              icon="archive"
              showArchiveTooltip
              size="h6"
              weight="medium"
              className="break-words min-w-0"
            />
            {status?.systemKey && (
              <Text
                size="tiny"
                className="rounded bg-secondary-100 px-2 py-1 text-secondary-700"
              >
                <PiLock className="inline h-3 w-3" />{' '}
                {t('crm.funnelStatus.system')}
              </Text>
            )}
          </div>

          {!!actionsMenu && (
            <div className="flex items-center gap-1">{actionsMenu}</div>
          )}
        </div>
        <FunnelStatusDescriptionBlocks
          description={status?.description ?? ''}
          statusReferences={referenceStatuses}
          statusId={status?.id}
          isAiLinked={isAiLinked}
        />
      </div>
    );
  }

  return (
    <form
      id={id}
      onSubmit={handleSubmit(submitHandler)}
      className="flex flex-col w-full gap-2"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        {isAiLinked && <AiBadge size="sm" appearance="outline" />}

        {actionsMenu && (
          <div className="ml-auto flex-shrink-0">{actionsMenu}</div>
        )}
      </div>

      <div className="flex flex-col gap-4 w-full">
        <LabelTextInput
          // TODO: check when implementing i18n
          label="Nombre"
          inputProps={{
            ...register('name'),
            'aria-label': 'Nombre',
            // TODO: check when implementing i18n
            placeholder: 'Nombra el estado',
            error: errors.name?.message,
            disabled: isLoading || isSubmitting,
          }}
          labelPosition="top"
        />

        <div className="flex flex-col gap-1 text-sm font-medium text-secondary-700">
          <label htmlFor={terminalOutcomeInputId}>
            {t('crm.funnelStatus.outcomeLabel')}
          </label>
          <Controller
            name="terminalOutcome"
            control={control}
            render={({ field }) => (
              <Select
                id={terminalOutcomeInputId}
                name={field.name}
                value={field.value}
                onBlur={field.onBlur}
                onChange={field.onChange}
                disabled={
                  isLoading || isSubmitting || Boolean(status?.systemKey)
                }
                aria-label={t('crm.funnelStatus.outcomeLabel')}
                options={[
                  { value: '', content: t('crm.funnelStatus.inProgress') },
                  {
                    value: 'positive',
                    content: t('crm.funnelStatus.closedPositive'),
                  },
                  {
                    value: 'negative',
                    content: t('crm.funnelStatus.closedNegative'),
                  },
                ]}
              />
            )}
          />
        </div>

        <input type="hidden" {...register('description')} />

        <div className="flex flex-col gap-2">
          <Text size="small" weight="medium">
            {/* TODO: check when implementing i18n */}
            {isPlainMode ? 'Instrucciones (texto plano)' : 'Instrucciones'}
          </Text>
          {isPlainMode ? (
            <textarea
              className="w-full resize-vertical border border-gray-200 rounded-md bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-primary-200 disabled:text-gray-400 placeholder-gray-400"
              rows={10}
              // TODO: check when implementing i18n
              placeholder={'Escribe las instrucciones para este estado...'}
              value={descriptionValue}
              onChange={(event) => handleDescriptionChange(event.target.value)}
              onKeyDown={(event) =>
                applyPlainDescriptionShortcut(
                  event,
                  isLoading || isSubmitting,
                  handleDescriptionChange
                )
              }
              disabled={isLoading || isSubmitting}
            />
          ) : (
            <FunnelStatusDescriptionEditor
              value={descriptionValue}
              onChange={handleDescriptionChange}
              disabled={isLoading || isSubmitting}
              referenceStatuses={referenceStatuses}
              isAiLinked={isAiLinked}
            />
          )}
          {errors.description?.message && (
            <Text size="tiny" color="danger">
              {errors.description.message}
            </Text>
          )}
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          isIconOnly
          onClick={handleSubmit(submitHandler)}
          loading={isLoading || isSubmitting}
          disabled={!isDirty || isLoading || isSubmitting}
        >
          <PiCheck className="h-5 w-5" />
        </Button>
        {showDelete && (
          <Button
            type="button"
            variant="danger"
            appearance="soft"
            size="sm"
            isIconOnly
            onClick={onDelete}
            loading={isLoading || isSubmitting}
            disabled={isLoading || isSubmitting}
          >
            <PiTrash className="h-5 w-5" />
          </Button>
        )}
        {onCancel && (
          <Button
            type="button"
            variant="neutral"
            appearance="ghost"
            size="sm"
            isIconOnly
            onClick={handleCancel}
            loading={isLoading || isSubmitting}
            disabled={isLoading || isSubmitting}
          >
            <PiX className="h-5 w-5" />
          </Button>
        )}
      </div>
    </form>
  );
}
